import React, {PureComponent, ReactNode} from "react"

import Hr from "@irati/ui/src/Layout/Hr"
import Flex from "@irati/ui/src/Flex"
import OptionTab from "./OptionTab"
import LoseChangesModal from "./LoseChangesModal"

interface Props {
  existingSelectionId?: number,
  options: Option[],
  areStepSettingsDefault?: () => Promise<boolean>,
  resetToDefault?: () => Promise<any>,
  handleOptionChange: (newSelection: Option) => void,
  setIsStepCompletable: (newSetting: boolean) => void
}

export interface Option {
  selectionId: number,
  title: string,
  description: string,
  component: ReactNode,
  actionRequiredForStepCompletion?: boolean
}

interface State {
  showOptions: boolean,
  selectedOptionId?: number,
  showPrompt?: boolean,
  optionToBeSelected?: Option,
  load?: Promise<any>
}

class OptionPicker extends PureComponent<Props, State> {
  state: State = {
    showOptions: true,
    selectedOptionId: this.props.existingSelectionId === 0 ? undefined : this.props.existingSelectionId
  }

  async componentDidMount() {
    const {existingSelectionId, areStepSettingsDefault, setIsStepCompletable, options} = this.props

    // If there are no options that have required actions step can be instantly completed
    if (!options.find((option) => option.actionRequiredForStepCompletion)) {
      return setIsStepCompletable(true)
    }

    if (existingSelectionId && areStepSettingsDefault) {
      const selectedOption = options.find((option: Option) => option.selectionId === existingSelectionId)
      const isDefault = await areStepSettingsDefault()
      // ensure the option selected meets requirements to mark as complete
      const completable =
        (selectedOption!.actionRequiredForStepCompletion && !isDefault) || !selectedOption!.actionRequiredForStepCompletion
      setIsStepCompletable(completable)
    }
  }

  getTabContent = (selectionId: number): ReactNode => {
    const {setIsStepCompletable, options} = this.props
    const selectedOption = options.find((option: Option) => option.selectionId === selectionId)
    if (!selectedOption) return null
    const newProps = {
      setIsStepCompletable,
      setOptionsVisibility: this.setOptionsVisibility
    }
    return React.cloneElement(selectedOption.component, newProps)
  }

  handleTabClick = (option: Option) => {
    if (this.state.selectedOptionId === option.selectionId) return
    // Check for changes to be reverted based on step + "has anything been changed?"
    // If yes, show modal to alert that we're going to revert the changes
    // shouldResetToDefault and resetToDefault are shared function call, the implementation itself depends on the step
    const {areStepSettingsDefault} = this.props

    if (!areStepSettingsDefault || !this.state.selectedOptionId) {
      this.selectOption(option)
    } else {
      areStepSettingsDefault().then((isDefault: boolean) => {
        if (isDefault) {
          this.selectOption(option)
        } else {
          this.showModal(option)
        }
      })
    }
  }

  setOptionsVisibility = (showOptions: boolean) => {
    this.setState({showOptions})
  }

  handleReset = () => {
    // @ts-ignore
    const load = this.props.resetToDefault().then(() => {
      this.selectOption(this.state.optionToBeSelected!)
      this.hideModal()
    })
    this.setState({load})
  }

  selectOption = (option: Option) => {
    this.setState({selectedOptionId: option.selectionId})
    this.props.handleOptionChange(option)
  }

  showModal = (option: Option) => {
    this.setState({
      optionToBeSelected: option,
      showPrompt: true
    })
  }

  hideModal = () => {
    this.setState({
      optionToBeSelected: undefined,
      showPrompt: false
    })
  }

  render() {
    const {options} = this.props
    const {showOptions, selectedOptionId, showPrompt, load} = this.state

    return (
      <>
        {showOptions && (
          <Flex justifyContent="space-between">
            {options.map((option: Option) => (
              <OptionTab
                key={`OptionTab${option.selectionId}-${option.title}`}
                selected={selectedOptionId === option.selectionId}
                {...option}
                handleClick={() => this.handleTabClick(option)} />
            ))}
          </Flex>
        )}

        {showOptions && selectedOptionId && <Hr />}

        {selectedOptionId && this.getTabContent(selectedOptionId)}

        <LoseChangesModal load={load} handleReset={this.handleReset} onHide={this.hideModal} show={showPrompt} />
      </>
    )
  }
}

export default OptionPicker
